import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Calendar, Banknote, Receipt as ReceiptIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PESO = "\u20B1";

function fmtMoney(n) {
  const v = Number(n || 0);
  return `${PESO}${v.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function todayStr() { return new Date().toISOString().slice(0,10); }

export default function POSReports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "hospital_admin";

  const [period, setPeriod] = useState("daily");
  const [start, setStart] = useState(todayStr());
  const [end, setEnd] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cash session form (admin only)
  const [openingCash, setOpeningCash] = useState(0);
  const [actualDrawer, setActualDrawer] = useState("");
  const [sessionNotes, setSessionNotes] = useState("");
  const [savingSession, setSavingSession] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") { params.start = start; params.end = end; }
      const r = await api.get("/pos/reports", { params });
      setReport(r.data || null);
      if (r.data?.cash_reconciliation) {
        setOpeningCash(r.data.cash_reconciliation.opening_cash || 0);
        if (r.data.cash_reconciliation.actual_drawer != null) {
          setActualDrawer(r.data.cash_reconciliation.actual_drawer);
        }
      }
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const saveSession = async (close = false) => {
    setSavingSession(true);
    try {
      const body = { opening_cash: Number(openingCash) || 0, notes: sessionNotes };
      if (close) body.actual_drawer = Number(actualDrawer) || 0;
      await api.post("/pos/cash-session", body);
      toast.success(close ? "Cash session closed" : "Opening cash saved");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setSavingSession(false);
    }
  };

  const r = report;

  return (
    <div className="p-5 lg:p-7 space-y-5" data-testid="pos-reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><TrendingUp size={20} className="text-blue-600" /> POS Reports</h1>
          <p className="text-sm text-slate-600">Daily collections, payments breakdown, and revenue analytics.</p>
        </div>
      </div>

      <Card className="p-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
        <div>
          <Label className="text-xs">Period</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily" data-testid="period-daily">Daily</SelectItem>
              <SelectItem value="weekly" data-testid="period-weekly">Weekly (last 7d)</SelectItem>
              <SelectItem value="monthly" data-testid="period-monthly">Monthly (last 30d)</SelectItem>
              <SelectItem value="custom" data-testid="period-custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {period === "custom" ? (
          <>
            <div>
              <Label className="text-xs">Start</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" />
            </div>
            <div>
              <Label className="text-xs">End</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" />
            </div>
            <Button onClick={load} data-testid="btn-run-report">Run</Button>
          </>
        ) : (
          <Button variant="outline" onClick={load} data-testid="btn-refresh-report">Refresh</Button>
        )}
        {r ? (
          <div className="text-xs text-slate-500 ml-auto flex items-center gap-1">
            <Calendar size={12} /> {r.start} → {r.end}
          </div>
        ) : null}
      </Card>

      {loading || !r ? (
        <div className="p-10 text-center text-slate-500">Loading…</div>
      ) : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
            <Kpi label="Gross Sales" value={fmtMoney(r.gross_sales)} icon={<ReceiptIcon size={14} />} testid="kpi-gross" />
            <Kpi label="Discounts" value={`-${fmtMoney(r.discounts)}`} red testid="kpi-discounts" />
            <Kpi label="Net Sales" value={fmtMoney(r.net_sales)} icon={<Banknote size={14} className="text-emerald-600" />} testid="kpi-net" />
            <Kpi label="Transactions" value={r.transaction_count} testid="kpi-tx-count" />
          </div>

          {/* Payments breakdown */}
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="p-5 lg:col-span-2" data-testid="payments-card">
              <div className="font-semibold text-slate-800 mb-3">Payments Breakdown</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(r.payments || {}).map(([method, amt]) => (
                  <div key={method} className="border rounded p-3" data-testid={`pay-${method.toLowerCase().replace(/ /g, "-")}`}>
                    <div className="text-xs uppercase tracking-wider text-slate-500">{method}</div>
                    <div className="text-lg font-bold text-slate-900 mt-1">{fmtMoney(amt)}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-3">
                Split payments: <span className="font-bold text-slate-700">{r.split_payment_count}</span> transaction(s)
              </div>
            </Card>

            <Card className="p-5" data-testid="revenue-card">
              <div className="font-semibold text-slate-800 mb-3">Revenue by Type</div>
              <div className="space-y-2">
                <SimpleRow label="Consultation" value={fmtMoney(r.revenue_by_type?.consultation)} testid="rev-consultation" />
                <SimpleRow label="Medicine" value={fmtMoney(r.revenue_by_type?.medicine)} testid="rev-medicine" />
                <SimpleRow label="Service" value={fmtMoney(r.revenue_by_type?.service)} testid="rev-service" />
              </div>
            </Card>
          </div>

          <Card className="p-5" data-testid="voided-card">
            <div className="font-semibold text-slate-800 mb-2">Voided Transactions</div>
            <div className="text-sm text-slate-600">
              <span className="font-bold text-rose-700">{r.voided_transactions?.count || 0}</span> voided · Total {fmtMoney(r.voided_transactions?.total)}
            </div>
          </Card>

          {/* Cash Reconciliation */}
          <Card className="p-5 space-y-3" data-testid="cash-reconciliation-card">
            <div className="font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck size={14} className="text-emerald-600" /> End-of-Day Cash Reconciliation</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Kpi label="Opening Cash" value={fmtMoney(r.cash_reconciliation?.opening_cash)} testid="rec-opening" />
              <Kpi label="Cash Collected" value={fmtMoney(r.cash_reconciliation?.cash_collected)} testid="rec-collected" />
              <Kpi label="Expected Drawer" value={fmtMoney(r.cash_reconciliation?.expected_drawer)} testid="rec-expected" />
              <Kpi label="Actual Drawer" value={r.cash_reconciliation?.actual_drawer != null ? fmtMoney(r.cash_reconciliation.actual_drawer) : "—"} testid="rec-actual" />
              <Kpi
                label="Variance"
                value={r.cash_reconciliation?.variance != null ? fmtMoney(r.cash_reconciliation.variance) : "—"}
                red={(r.cash_reconciliation?.variance || 0) < 0}
                testid="rec-variance"
              />
            </div>

            {isAdmin ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t" data-testid="cash-session-form">
                <div>
                  <Label>Opening Cash (₱)</Label>
                  <Input type="number" min="0" step="0.01" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)} data-testid="session-opening" />
                </div>
                <div>
                  <Label>Actual Drawer (₱) — for closing</Label>
                  <Input type="number" min="0" step="0.01" value={actualDrawer} onChange={(e) => setActualDrawer(e.target.value)} data-testid="session-actual" />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input value={sessionNotes} onChange={(e) => setSessionNotes(e.target.value)} data-testid="session-notes" />
                </div>
                <div className="md:col-span-3 flex gap-2">
                  <Button variant="outline" onClick={() => saveSession(false)} disabled={savingSession} data-testid="btn-save-opening">
                    Save Opening Cash
                  </Button>
                  <Button onClick={() => saveSession(true)} disabled={savingSession} data-testid="btn-close-session">
                    Close Today's Session
                  </Button>
                </div>
              </div>
            ) : null}
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon, red = false, testid }) {
  return (
    <div className="border rounded p-3 bg-white" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">{icon}{label}</div>
      <div className={`text-lg font-bold mt-1 ${red ? "text-rose-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function SimpleRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}
